import { useEffect, useState } from 'react';

// Tailwind default breakpoints. Keep in sync with `client/tailwind.config.js`.
export const MOBILE_MAX = 639;   // < sm
export const TABLET_MAX = 1023;  // < lg
export const DESKTOP_MAX = 1279; // < xl

const DEFAULT_DESKTOP_WIDTH = 1280;
const DEBOUNCE_MS = 100;

const isBrowser = typeof window !== 'undefined';

const subscribers = new Set();
let currentWidth = isBrowser ? window.innerWidth : DEFAULT_DESKTOP_WIDTH;
let listenerAttached = false;
let debounceHandle = null;

function notify() {
  subscribers.forEach((fn) => fn(currentWidth));
}

function onResize() {
  if (debounceHandle) clearTimeout(debounceHandle);
  debounceHandle = setTimeout(() => {
    currentWidth = window.innerWidth;
    notify();
  }, DEBOUNCE_MS);
}

function ensureListener() {
  if (!isBrowser || listenerAttached) return;
  window.addEventListener('resize', onResize);
  listenerAttached = true;
}

function subscribe(fn) {
  ensureListener();
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function deriveState(width) {
  return {
    width,
    isMobile: width <= MOBILE_MAX,
    isTablet: width > MOBILE_MAX && width <= TABLET_MAX,
    isDesktop: width > TABLET_MAX,
    isXL: width > DESKTOP_MAX,
  };
}

/**
 * Single source of truth for viewport-based responsive decisions.
 *
 * Use this when a responsive choice cannot be expressed in Tailwind
 * utility classes (e.g. rendering a different component on mobile vs
 * desktop). For pure styling, prefer Tailwind's `sm:`/`md:`/`lg:`/`xl:`
 * variants — the breakpoint constants exported here match Tailwind's
 * defaults so the two stay in sync.
 *
 * One module-level resize listener serves every consumer, debounced
 * 100ms. SSR-safe: returns desktop defaults when `window` is undefined.
 *
 * @returns {{ width: number, isMobile: boolean, isTablet: boolean, isDesktop: boolean, isXL: boolean }}
 */
export default function useViewport() {
  const [width, setWidth] = useState(() => (isBrowser ? window.innerWidth : DEFAULT_DESKTOP_WIDTH));

  useEffect(() => {
    if (!isBrowser) return undefined;
    // Sync with the latest module-level width in case a resize fired
    // before this consumer mounted.
    setWidth(currentWidth);
    return subscribe(setWidth);
  }, []);

  return deriveState(width);
}
